import { mapListToStore } from 'app/area/codetable/list/mapping/map-list-to-store';
import {
  FailAxes,
  FailCaseTypes,
  FailCells, FailDetails,
  FailRiskAreas,
  FailSaveCells,
  ReceiveAxes,
  ReceiveCaseTypes,
  ReceiveCells, ReceiveDetails,
  ReceiveRiskAreas,
  ReceiveSaveCells,
  RequestAxes,
  RequestCaseTypes,
  RequestCells, RequestDetails,
  RequestRiskAreas,
  RequestSaveCells,
  SelectCell,
  SetThirdAxisItem,
  SetType,
  UpdateModifiedCells
} from 'app/area/risk-matrix-administration/action-types';
import { api } from 'app/area/risk-matrix-administration/api';
import { mapCaseTypesToStore } from 'app/area/risk-matrix-administration/mappers/map-case-types-to-store';
import { mapCellsToStore } from 'app/area/risk-matrix-administration/mappers/map-cells-to-store';
import { mapDetailsToStore } from 'app/area/risk-matrix-administration/mappers/map-details-to-store';
import { mapToAxes } from 'app/area/risk-matrix-administration/mappers/map-to-axes';
import { getAxisProperty } from 'app/area/risk-matrix-administration/mappers/map-to-risk-matrix-definition';
import { RiskMatrixCell, RiskMatrixDetails, SetCellPayload, UpdateSelectedCellPayload } from 'app/area/risk-matrix-administration/types-store';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { ActionDispatching } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { ComplexValue } from 'ui';

export const actionCreators = {

  setType: (type: ComplexValue<number>): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new SetType(type));
    },

  setThirdAxisItem: (thirdAxisItem: ComplexValue<number>): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new SetThirdAxisItem(thirdAxisItem));
    },

  clearThirdAxisItem: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new SetThirdAxisItem(undefined));
    },

  selectCell: ({ xAxisItemIndex, yAxisItemIndex, axes, cells, thirdAxisItem }: SetCellPayload): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      const xAxisItem = axes[0]?.items?.[xAxisItemIndex];
      const yAxisItem = axes[1]?.items?.[yAxisItemIndex];

      const xAxisProperty = getAxisProperty(axes[0]?.codetableId);
      const yAxisProperty = getAxisProperty(axes[1]?.codetableId);
      const zAxisProperty = getAxisProperty(axes[2]?.codetableId);

      const selectedCell = cells.find(cell => cell[xAxisProperty] === xAxisItem.id &&
        (!yAxisProperty || cell[yAxisProperty] === yAxisItem.id) &&
        (!thirdAxisItem || cell[zAxisProperty] === thirdAxisItem.value))?.id;

      selectedCell && dispatch(new SelectCell(selectedCell));
    },

  updateSelectedCell: ({ patch, selectedCellId, cells, modifiedCells }: UpdateSelectedCellPayload): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      if (modifiedCells.find(cell => cell.id === selectedCellId)) {
        dispatch(new UpdateModifiedCells(modifiedCells.map(cell => cell.id === selectedCellId ? { ...cell, ...patch } : cell)));
      } else {
        const cellToModify = cells.find(cell => cell.id === selectedCellId);

        cellToModify && dispatch(new UpdateModifiedCells([...modifiedCells, ({ ...cellToModify, ...patch })]));
      }
    },

  clearModifiedCells: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new UpdateModifiedCells([]));
    },

  clearSelectedCell: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new SelectCell(undefined));
    },

  loadDetails: (type: ComplexValue<number>): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestDetails());

      try {
        const details = await api.loadDetails(type);

        dispatch(new ReceiveDetails(mapDetailsToStore(details)));

      } catch (err) {
        dispatch(new FailDetails());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    },

  loadCaseTypes: (type: ComplexValue<number>): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestCaseTypes());

      try {
        const caseTypes = await api.loadCaseTypes(type);

        dispatch(new ReceiveCaseTypes(mapCaseTypesToStore(caseTypes)));

      } catch (err) {
        dispatch(new FailCaseTypes());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    },

  loadAxes: (details: RiskMatrixDetails): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestAxes());

      try {
        const axesIds = [details.xAxisCodetableId, details.yAxisCodetableId, details.zAxisCodetableId];
        const axesToLoad = axesIds
          .filter(axis => axis)
          .map(axis => api.loadAxis(axis));

        const axes = await Promise.all(axesToLoad);

        dispatch(new ReceiveAxes(mapToAxes(axes, axesIds,
          [details.xAxisSortOrderType, details.yAxisSortOrderType, details.zAxisSortOrderType]
        )));

      } catch (err) {
        dispatch(new FailAxes());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    },

  loadRiskAreas: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestRiskAreas());

      try {
        const riskAreas = await api.loadRiskAreas();

        dispatch(new ReceiveRiskAreas(mapListToStore(riskAreas?.results)));

      } catch (err) {
        dispatch(new FailRiskAreas());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    },

  loadCells: (type: ComplexValue<number>): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestCells());

      try {
        const cells = await api.loadCells(type);

        dispatch(new ReceiveCells(mapCellsToStore(cells?.data?.results)));

      } catch (err) {
        dispatch(new FailCells());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    },

  saveCells: (cells: RiskMatrixCell[], successText: string, type: ComplexValue<number>): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestSaveCells());

      try {
        await api.saveCells(cells);
        dispatch(new ReceiveSaveCells());
        dispatch(new SuccessNotification(successText));
        dispatch(new UpdateModifiedCells([]));
        dispatch(actionCreators.loadCells(type));
      } catch (err) {
        dispatch(new FailSaveCells());

        const error = err?.userMessages || err?.message;
        error && dispatch(new ErrorNotification(error));

      }
    }
};
